import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';

// calendar_events not yet in generated types — cast to any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export type EventType = 'meeting' | 'reminder' | 'other' | 'unavailable';
export type EventStatus = 'scheduled' | 'canceled' | 'completed';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  type: EventType;
  status: EventStatus;
  color: string | null;
  created_by: string | null;
  attendee_ids: string[];
  contact_id: string | null;
  deal_id: string | null;
  daily_room_url: string | null;
  daily_room_name: string | null;
  audio_url?: string | null;
  created_at: string;
  updated_at: string;
}

export type CalendarEventInsert = Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>;
export type CalendarEventUpdate = Partial<CalendarEventInsert>;

// ── Queries ───────────────────────────────────────────────────────────────────

export function useCalendarEvents(year: number, month: number, filterUserId?: string | null) {
  const isValidDate = Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12;
  const monthStart = isValidDate ? startOfMonth(new Date(year, month - 1)).toISOString() : null;
  const monthEnd = isValidDate ? endOfMonth(new Date(year, month - 1)).toISOString() : null;

  return useQuery({
    queryKey: ['calendar-events', year, month, filterUserId ?? 'self'],
    enabled: isValidDate,
    queryFn: async (): Promise<CalendarEvent[]> => {
      let query = supabase
        .from('calendar_events')
        .select('*')
        .gte('start_at', monthStart)
        .lte('start_at', monthEnd)
        .neq('status', 'canceled')
        .order('start_at', { ascending: true });

      // Manager filtering by a specific user
      if (filterUserId) {
        query = query.or(`created_by.eq.${filterUserId},attendee_ids.cs.{${filterUserId}}`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as CalendarEvent[];
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCalendarEventMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  const createEvent = useMutation({
    mutationFn: async (data: CalendarEventInsert) => {
      const { data: result, error } = await supabase
        .from('calendar_events')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return result as CalendarEvent;
    },
    onSuccess: () => { invalidate(); toast.success('Evento criado'); },
    onError: () => toast.error('Erro ao criar evento'),
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & CalendarEventUpdate) => {
      const { data: result, error } = await supabase
        .from('calendar_events')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result as CalendarEvent;
    },
    onSuccess: () => { invalidate(); toast.success('Evento atualizado'); },
    onError: () => toast.error('Erro ao atualizar evento'),
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Sem permissão para excluir este evento');
    },
    onSuccess: () => { invalidate(); toast.success('Evento excluído'); },
    onError: (err: Error) => toast.error(err.message || 'Erro ao excluir evento'),
  });

  return { createEvent, updateEvent, deleteEvent };
}
